import dagre from '@dagrejs/dagre';

import {
  IKiKontrollGesellschafter,
  IKiKontrollLeiter,
  IKiKontrollStatus,
} from '../Interfaces/IKi';

/**
 * Eigentümer- und Kontrollstruktur als Graph (#1336) — ohne Oberfläche, damit
 * testbar. Kanten zeigen vom Gesellschafter auf die Gesellschaft, an der er
 * beteiligt ist; die Ausgangsgesellschaft ist die Wurzel.
 */

/** Bis zu dieser Ebene über der Ausgangsgesellschaft wird gesucht. */
export const MAX_TIEFE = 5;
/** So viele Gesellschaften werden je Prüfung im Register abgerufen — das
 *  Registerportal erlaubt 60 Abrufe je Stunde. */
export const MAX_ABRUFE = 10;
/** Wirtschaftlich berechtigt ist, wer MEHR als 25 % hält oder kontrolliert
 *  (§ 3 Abs. 2 S. 1 GwG). Genau 25 % reichen nicht. */
export const GWG_SCHWELLE = 0.25;
/** Kontrolle über eine Zwischengesellschaft: Mehrheit, also MEHR als 50 %
 *  (§ 3 Abs. 2 S. 2–4 GwG mit § 290 Abs. 2–4 HGB). */
export const KONTROLL_SCHWELLE = 0.5;

export type KnotenStatus =
  | IKiKontrollStatus
  /** noch nicht abgerufen */
  | 'offen'
  /** wird gerade abgerufen */
  | 'laeuft'
  /** Tiefe oder Abruf-Grenze erreicht */
  | 'grenze'
  /** Ringbeteiligung — schon weiter unten in der Kette */
  | 'ring';

export interface KontrollKnoten {
  key: string;
  art: 'person' | 'gesellschaft';
  bezeichnung: string;
  ebene: number;
  status?: KnotenStatus;
  grund?: string | null;
  quelle?: string | null;
  register?: string | null;
  /** Für die Suche im Register: so, wie die Liste die Gesellschaft nennt. */
  eintrag?: IKiKontrollGesellschafter;
  /** Person: Geburtsdatum, Ort. */
  detail?: string | null;
  /** Gesellschaft ohne natürliche Person dahinter: wer sie leitet. */
  geschaeftsleiter?: IKiKontrollLeiter[];
  /** Die Leitung steht in keinem Register — von Hand ergänzen. */
  leitungManuell?: boolean;
}

export interface KontrollKante {
  von: string;
  nach: string;
  anteil: number | null;
  rolle?: string | null;
  hafteinlage?: number | null;
}

export interface KontrollGraph {
  wurzel: string;
  knoten: Record<string, KontrollKnoten>;
  kanten: KontrollKante[];
}

const norm = (text: string | null | undefined): string =>
  (text ?? '')
    .toLowerCase()
    .replace(/[„"“”'.,()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Wiedererkennen über die Kette hinweg. Gesellschaften am Namen — die
 * Registernummer fehlt in vielen Listen; Personen an Name und Geburtsdatum.
 */
export const schluessel = (g: IKiKontrollGesellschafter): string =>
  g.art === 'gesellschaft'
    ? `firma:${norm(g.Firma || g.Bezeichnung)}`
    : `person:${norm(g.Bezeichnung)}|${g.Geburtsdatum ?? ''}`;

export const neuerGraph = (
  wurzel: IKiKontrollGesellschafter,
  quelle?: string | null
): KontrollGraph => {
  const key = schluessel(wurzel);
  return {
    wurzel: key,
    knoten: {
      [key]: {
        key,
        art: 'gesellschaft',
        bezeichnung: wurzel.Bezeichnung,
        ebene: 0,
        status: 'ok',
        quelle,
        register: wurzel.Registernummer ?? null,
        eintrag: wurzel,
      },
    },
    kanten: [],
  };
};

/**
 * Die Gesellschafter einer Gesellschaft eintragen. Gibt die Schlüssel der
 * Gesellschaften zurück, die NEU hinzugekommen sind — die sind als Nächstes
 * zu prüfen. Eine Gesellschaft, die schon im Graphen steht, wird nur
 * verbunden (Ringbeteiligung oder dieselbe Holding auf zwei Wegen).
 */
export const gesellschafterEintragen = (
  graph: KontrollGraph,
  gesellschaftKey: string,
  gesellschafter: IKiKontrollGesellschafter[]
): { graph: KontrollGraph; neu: string[] } => {
  const knoten = { ...graph.knoten };
  const kanten = [...graph.kanten];
  const neu: string[] = [];
  const ebene = (knoten[gesellschaftKey]?.ebene ?? 0) + 1;

  for (const g of gesellschafter) {
    const key = schluessel(g);
    if (!knoten[key]) {
      knoten[key] = {
        key,
        art: g.art,
        bezeichnung: g.Bezeichnung,
        ebene,
        status: g.art === 'gesellschaft' ? 'offen' : undefined,
        register: g.Registernummer ?? null,
        eintrag: g,
        detail:
          g.art === 'person'
            ? [g.Geburtsdatum ? `*${g.Geburtsdatum}` : null, g.Ort]
                .filter(Boolean)
                .join(', ') || null
            : null,
      };
      if (g.art === 'gesellschaft') {
        neu.push(key);
      }
    }
    if (!kanten.some(k => k.von === key && k.nach === gesellschaftKey)) {
      kanten.push({
        von: key,
        nach: gesellschaftKey,
        anteil: g.Anteil ?? null,
        rolle: g.Rolle ?? null,
        hafteinlage: g.Hafteinlage ?? null,
      });
    }
  }

  return { graph: { ...graph, knoten, kanten }, neu };
};

export const knotenSetzen = (
  graph: KontrollGraph,
  key: string,
  aenderung: Partial<KontrollKnoten>
): KontrollGraph => ({
  ...graph,
  knoten: { ...graph.knoten, [key]: { ...graph.knoten[key], ...aenderung } },
});

export interface Durchgerechnet {
  /** Anteil an der Ausgangsgesellschaft, 0..1; `null`, wenn unterwegs ein
   *  Anteil fehlt (z. B. KG ohne Prozente). */
  anteil: number | null;
  /** Mindestens ein Weg ohne bekannten Anteil — dann ist `anteil` (falls
   *  gesetzt) nur eine Untergrenze. */
  unvollstaendig: boolean;
}

/**
 * Durchgerechneter Anteil je Knoten an der Ausgangsgesellschaft: Summe über
 * alle Wege, je Weg das Produkt der Anteile. Ringbeteiligungen werden nicht
 * im Kreis verfolgt.
 */
export const durchrechnen = (
  graph: KontrollGraph
): Record<string, Durchgerechnet> => {
  const ergebnis: Record<string, Durchgerechnet> = {};
  const ausgehend: Record<string, KontrollKante[]> = {};
  for (const k of graph.kanten) {
    (ausgehend[k.von] ??= []).push(k);
  }

  const rechne = (key: string, besucht: Set<string>): Durchgerechnet => {
    if (key === graph.wurzel) {
      return { anteil: 1, unvollstaendig: false };
    }
    if (besucht.has(key)) {
      return { anteil: 0, unvollstaendig: false };
    }
    const weiter = new Set(besucht).add(key);
    let summe = 0;
    let bekannt = false;
    let unvollstaendig = false;
    for (const kante of ausgehend[key] ?? []) {
      const oben = rechne(kante.nach, weiter);
      if (kante.anteil === null || oben.anteil === null) {
        unvollstaendig = true;
        continue;
      }
      summe += kante.anteil * oben.anteil;
      bekannt = true;
      unvollstaendig = unvollstaendig || oben.unvollstaendig;
    }
    return { anteil: bekannt ? summe : null, unvollstaendig };
  };

  for (const key of Object.keys(graph.knoten)) {
    ergebnis[key] = rechne(key, new Set());
  }
  return ergebnis;
};

/** Natürliche Personen am Ende der Kette, größter Anteil zuerst. */
export const natuerlichePersonen = (graph: KontrollGraph) => {
  const anteile = durchrechnen(graph);
  return Object.values(graph.knoten)
    .filter(k => k.art === 'person')
    .map(k => ({ knoten: k, ...anteile[k.key] }))
    .sort((a, b) => (b.anteil ?? -1) - (a.anteil ?? -1));
};

export const KNOTEN_BREITE = 230;
export const KNOTEN_HOEHE = 76;
/** Abstand zwischen Gesellschaft und ihrem Kasten „Geschäftsleitung". */
export const LEITUNG_ABSTAND = 40;

/** Hat dieser Knoten einen Kasten „Geschäftsleitung" neben sich? */
export const hatLeitung = (k: KontrollKnoten): boolean =>
  k.art === 'gesellschaft' &&
  ((k.geschaeftsleiter?.length ?? 0) > 0 || Boolean(k.leitungManuell));

export const leitungKey = (key: string) => `leitung:${key}`;

/** Höhe des Leitungskastens — eine Zeile je Person. */
export const leitungHoehe = (k: KontrollKnoten): number =>
  Math.max(
    KNOTEN_HOEHE,
    30 + 18 * Math.max(1, k.geschaeftsleiter?.length ?? 0)
  );

/**
 * Positionen fürs Organigramm: Gesellschafter oben, Gesellschaft darunter.
 * Endet die Kette an einer Gesellschaft, steht ihre Geschäftsleitung rechts
 * daneben — dafür bekommt sie im Layout doppelte Breite.
 */
export const anordnen = (
  graph: KontrollGraph
): Record<string, { x: number; y: number }> => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 70 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const k of Object.values(graph.knoten)) {
    g.setNode(k.key, {
      width: hatLeitung(k)
        ? 2 * KNOTEN_BREITE + LEITUNG_ABSTAND
        : KNOTEN_BREITE,
      height: hatLeitung(k) ? leitungHoehe(k) : KNOTEN_HOEHE,
    });
  }
  for (const k of graph.kanten) {
    g.setEdge(k.von, k.nach);
  }
  dagre.layout(g);

  const pos: Record<string, { x: number; y: number }> = {};
  for (const k of Object.values(graph.knoten)) {
    const n = g.node(k.key);
    if (hatLeitung(k)) {
      const links = n.x - (2 * KNOTEN_BREITE + LEITUNG_ABSTAND) / 2;
      pos[k.key] = { x: links, y: n.y - KNOTEN_HOEHE / 2 };
      pos[leitungKey(k.key)] = {
        x: links + KNOTEN_BREITE + LEITUNG_ABSTAND,
        y: n.y - leitungHoehe(k) / 2,
      };
    } else {
      pos[k.key] = { x: n.x - KNOTEN_BREITE / 2, y: n.y - KNOTEN_HOEHE / 2 };
    }
  }
  return pos;
};

export const prozent = (anteil: number | null | undefined): string =>
  anteil === null || anteil === undefined
    ? ''
    : `${(anteil * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;

/** Beschriftung einer Kante: „50 %", „Kommanditist · 10.000 €" … */
export const kantenText = (k: KontrollKante): string =>
  [
    k.anteil !== null ? prozent(k.anteil) : null,
    k.anteil === null && k.rolle ? k.rolle : null,
    k.hafteinlage
      ? k.hafteinlage.toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 0,
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Alte Listen nennen nur Stammeinlagen, kein Kapital und keine Prozente.
 * Die Einlagen aller Gesellschafter ergeben zusammen das Kapital — daraus
 * rechnen, aber nur, wenn KEIN Anteil bekannt ist und JEDER einen
 * Nennbetrag hat (wie im Backend `anteileAusNennbetraegen`).
 */
export const anteileAusNennbetraegen = (
  gesellschafter: IKiKontrollGesellschafter[]
): IKiKontrollGesellschafter[] => {
  if (
    gesellschafter.length === 0 ||
    gesellschafter.some(g => g.Anteil !== null && g.Anteil !== undefined) ||
    gesellschafter.some(g => !g.Nennbetrag || g.Nennbetrag <= 0)
  ) {
    return gesellschafter;
  }
  const summe = gesellschafter.reduce((s, g) => s + (g.Nennbetrag ?? 0), 0);
  return gesellschafter.map(g => ({
    ...g,
    Anteil: (g.Nennbetrag ?? 0) / summe,
  }));
};

export interface WirtschaftlichBerechtigter {
  knoten: KontrollKnoten;
  /** Was die Person an der Ausgangsgesellschaft hält oder kontrolliert. */
  anteil: number;
  /** „unmittelbar 30 %", „beherrscht Holding GmbH (60 %), die 30 % hält" */
  grund: string;
}

export interface GwGErgebnis {
  berechtigte: WirtschaftlichBerechtigter[];
  /** Ein fehlender Anteil (KG ohne Prozente) könnte das Ergebnis ändern. */
  unsicher: boolean;
  /** Gesellschaften, an denen die Kette endet — dahinter kann noch jemand
   *  stehen (AG, ausländisch, nicht gefunden …). */
  offeneEnden: KontrollKnoten[];
}

/**
 * Wirtschaftlich Berechtigte nach § 3 Abs. 2 GwG.
 *
 * Nicht das Produkt der Anteile entscheidet, sondern **Kontrolle**: Wer an
 * einer Zwischengesellschaft mehr als 50 % hält (allein oder über weitere
 * Gesellschaften, die er schon beherrscht), dem wird deren Beteiligung voll
 * zugerechnet. Wirtschaftlich berechtigt ist, wem so mehr als 25 % der
 * Ausgangsgesellschaft zuzurechnen sind.
 *
 * Beispiel: A hält 60 % der Holding, die 30 % hält → A ist berechtigt
 * (durchgerechnet nur 18 %). B hält 50 % einer Holding mit 60 % → B nicht
 * (durchgerechnet 30 %, aber keine Kontrolle). Stimmrechte, die vom Kapital
 * abweichen, zeigt das Register nicht.
 */
export const wirtschaftlichBerechtigte = (
  graph: KontrollGraph
): GwGErgebnis => {
  const eingehend: Record<string, KontrollKante[]> = {};
  for (const k of graph.kanten) {
    (eingehend[k.nach] ??= []).push(k);
  }
  const unsicher = graph.kanten.some(k => k.anteil === null);

  // Was ein Halter (Person + von ihr beherrschte Gesellschaften) an `ziel` hat.
  const zugerechnet = (halter: Set<string>, ziel: string) =>
    (eingehend[ziel] ?? [])
      .filter(k => halter.has(k.von))
      .reduce((summe, k) => summe + (k.anteil ?? 0), 0);

  const berechtigte: WirtschaftlichBerechtigter[] = [];
  for (const person of Object.values(graph.knoten)) {
    if (person.art !== 'person') {
      continue;
    }
    const halter = new Set<string>([person.key]);
    const beherrscht: string[] = [];
    let geaendert = true;
    while (geaendert) {
      geaendert = false;
      for (const g of Object.values(graph.knoten)) {
        if (
          g.art === 'gesellschaft' &&
          g.key !== graph.wurzel &&
          !halter.has(g.key) &&
          zugerechnet(halter, g.key) > KONTROLL_SCHWELLE
        ) {
          halter.add(g.key);
          beherrscht.push(g.key);
          geaendert = true;
        }
      }
    }

    const anteil = zugerechnet(halter, graph.wurzel);
    if (anteil <= GWG_SCHWELLE) {
      continue;
    }

    const teile: string[] = [];
    const direkt = zugerechnet(new Set([person.key]), graph.wurzel);
    if (direkt > 0) {
      teile.push(`unmittelbar ${prozent(direkt)}`);
    }
    for (const key of beherrscht) {
      const ueber = (eingehend[graph.wurzel] ?? [])
        .filter(k => k.von === key)
        .reduce((summe, k) => summe + (k.anteil ?? 0), 0);
      const kontrolle = zugerechnet(
        new Set(Array.from(halter).filter(h => h !== key)),
        key
      );
      teile.push(
        `beherrscht ${graph.knoten[key].bezeichnung} (${prozent(kontrolle)})` +
          (ueber > 0 ? `, die ${prozent(ueber)} hält` : '')
      );
    }
    berechtigte.push({ knoten: person, anteil, grund: teile.join('; ') });
  }

  berechtigte.sort((a, b) => b.anteil - a.anteil);
  const offeneEnden = Object.values(graph.knoten).filter(
    k =>
      k.art === 'gesellschaft' &&
      k.key !== graph.wurzel &&
      k.status !== 'ok' &&
      k.status !== 'ring'
  );
  return { berechtigte, unsicher, offeneEnden };
};

const leiterText = (l: IKiKontrollLeiter) =>
  `${l.Bezeichnung}${l.Funktion ? ` (${l.Funktion})` : ''}`;

/** Für `POST /ki/kontrollstruktur/speichern` — der Inhalt des PDF. */
export const berichtBauen = (graph: KontrollGraph, hinweise: string[] = []) => {
  const wurzel = graph.knoten[graph.wurzel];
  const gwg = wirtschaftlichBerechtigte(graph);
  const anteile = durchrechnen(graph);
  return {
    Firma: wurzel.bezeichnung,
    Register: wurzel.register ?? null,
    WirtschaftlichBerechtigte: gwg.berechtigte.map(b => ({
      Bezeichnung: b.knoten.bezeichnung,
      Detail: b.knoten.detail ?? null,
      Anteil: prozent(b.anteil),
      Grund: b.grund,
    })),
    Fiktiv:
      gwg.berechtigte.length === 0
        ? {
            Text: FIKTIV_TEXT(wurzel.bezeichnung),
            Personen: (wurzel.geschaeftsleiter ?? []).map(l => ({
              Bezeichnung: l.Bezeichnung,
              Funktion: l.Funktion ?? null,
            })),
          }
        : null,
    Personen: natuerlichePersonen(graph).map(p => ({
      Bezeichnung: p.knoten.bezeichnung,
      Detail: p.knoten.detail ?? null,
      Anteil:
        p.anteil === null
          ? 'Anteil unbekannt'
          : `durchgerechnet ${p.unvollstaendig ? 'mind. ' : ''}${prozent(p.anteil)}`,
    })),
    Enden: gwg.offeneEnden.map(k => ({
      Bezeichnung: k.bezeichnung,
      Anteil:
        typeof anteile[k.key]?.anteil === 'number'
          ? prozent(anteile[k.key].anteil)
          : null,
      Status: k.status ?? null,
      Grund: k.grund ?? null,
      Geschaeftsleiter: (k.geschaeftsleiter ?? []).map(l => ({
        Bezeichnung: l.Bezeichnung,
        Funktion: l.Funktion ?? null,
      })),
    })),
    Gesellschaften: Object.values(graph.knoten)
      .filter(k => k.art === 'gesellschaft' && (k.quelle || k.register))
      .map(k => ({
        Bezeichnung: k.bezeichnung,
        Register: k.register ?? null,
        Quelle: k.quelle ?? null,
      })),
    Hinweise: [
      ...(gwg.unsicher
        ? [
            'Nicht alle Anteile sind bekannt (z. B. KG ohne Prozente) — das Ergebnis kann sich ändern.',
          ]
        : []),
      ...hinweise,
    ],
  };
};

export const FIKTIV_TEXT = (firma: string) =>
  `Keine natürliche Person hält oder kontrolliert mehr als 25 %. Bleibt es ` +
  `nach umfassender Prüfung (auch Transparenzregister) dabei, gilt als ` +
  `wirtschaftlich Berechtigter der gesetzliche Vertreter bzw. geschäfts` +
  `führende Gesellschafter des Vertragspartners ${firma} (§ 3 Abs. 2 S. 5 GwG).`;

export { leiterText };

/**
 * Die zuletzt im Chat erstellte Struktur — damit „speichere sie in Vorgang X"
 * auch eine Antwort später noch geht (die Karte von vorhin ist dann weg).
 * Bewusst nur im Speicher: nach dem Neuladen gibt es sie nicht mehr.
 */
let letzteStruktur: KontrollGraph | null = null;
export const kontrollStore = {
  get: () => letzteStruktur,
  set: (graph: KontrollGraph) => {
    letzteStruktur = graph;
  },
};
